import { Box } from '@mui/material';
import {
  QAsyncTablePagination,
  QCard,
  QCardContent,
  QTabContext,
  QTabList,
  QTab,
  QTabPanel,
  QTable,
  QTableBody,
  QTableRow,
  QTableCell,
} from '@kw/quokka-ui';
import { useEffect, useState } from 'react';
import CountryFlag from 'react-country-flag';
import PublicIcon from '@mui/icons-material/Public';
import { styled } from '@mui/material/styles';
import _ from 'lodash';
import { useLocalStorage } from '../../../../utils/useLocalStorage';
import { countriesMap } from '../../../../utils/countriesMap';
import { useHighRiskCountries } from '../../hooks';
import { NetworkInfoMap } from '../NetworkInfoMap';
import { PageLoader } from '../../../../PageLoader';
import { useQAsyncTablePagination } from '../../../shared/useQAsyncTablePagination';
import { useReloadRestore } from '../utils/useReloadRestore';
import { ApplicationDetails } from '../types/ApplicationDetailsType';

export interface CountryMapInfo {
  countryCode: string;
  count: number;
  countryName: string;
  latitude: number;
  longitude: number;
  domains: string[];
  isHighRisk?: boolean;
}

const EmptyDataRow = ({ title }: { title: string }) => (
  <QTableRow>
    <QTableCell align="center" sx={{ py: '40px' }}>
      No {title} Found
    </QTableCell>
  </QTableRow>
);

export const NetworkInfoTab = ({ appDetails }: ApplicationDetails['current']) => {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [countryData, setCountryData] = useState<{ [key: string]: CountryMapInfo }>({});
  const [domains, setDomains] = useState<{ country: string; domain: string; isHighRisk: boolean }[]>([]);
  const [loading, setLoading] = useState(false);
  const [tabPanelHeight, setTabPanelHeight] = useState(600);

  const { appDetailReportTabFocus, setAppDetailReportTabFocus, setShouldRestore } = useReloadRestore();
  const [activeTab, setActiveTab] = useState<number>(appDetailReportTabFocus.network);

  useEffect(() => {
    setAppDetailReportTabFocus({ ...appDetailReportTabFocus, root: 1, network: activeTab });
    setShouldRestore(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleTabChange = (_event, newTabValue) => {
    setActiveTab(newTabValue);
  };

  const { page, pageSize, pageSizeOptions, paginationOptions, setPaginationOptions, handleChangePage, handlePageSizeChange } =
    useQAsyncTablePagination();

  const { highRiskCountries = [], highRiskCountriesError } = useHighRiskCountries(accessToken);

  useEffect(() => {
    // format map and domains data
    setLoading(true);
    const mapData: { [key: string]: CountryMapInfo } = {};
    const domainsContacted: { country: string; domain: string; isHighRisk: boolean }[] = [];
    appDetails?.domainsContacted.forEach(({ domain, country }) => {
      // if country is empty, set it to worldwide and add to domainsContacted but not mapData
      if (country === '' && !domainsContacted.some(item => item.domain === domain)) {
        domainsContacted.push({ country: 'worldwide', domain, isHighRisk: false });
        return;
      }
      // aggregate unique domains and countries
      if (!domainsContacted.some(item => item.country === country && item.domain === domain)) {
        domainsContacted.push({ country, domain, isHighRisk: highRiskCountries?.includes(country) });
      }
      // format map data
      if (!mapData[country]) {
        const countryInfo = {
          countryCode: country,
          count: 1,
          countryName: countriesMap[country].countryName,
          latitude: countriesMap[country].latitude,
          longitude: countriesMap[country].longitude,
          domains: [domain],
        };
        mapData[country] = countryInfo;
      }
      if (!mapData[country].domains.includes(domain)) {
        mapData[country].count += 1;
        mapData[country].domains.push(domain);
      }
    });
    setCountryData(mapData);
    setDomains(domainsContacted);
    setLoading(false);
  }, [appDetails, highRiskCountries]);

  // Updates pagination options on load or when pageSize changes
  useEffect(() => {
    setPaginationOptions({
      total: domains.length,
      lastPage: Math.ceil(domains.length / pageSize),
      perPage: pageSize,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domains, pageSize]);

  // fixes the height of all tab panels to match the domains table
  const domainsTableHeight = document.getElementById('domains-table')?.offsetHeight;
  useEffect(() => {
    if (!tabPanelHeight) {
      setTabPanelHeight(domainsTableHeight);
    }
  }, [domainsTableHeight, tabPanelHeight]);

  const isLoading = !countryData && !highRiskCountries && !highRiskCountriesError;
  return (
    <Box>
      <Box>
        <QCard>
          <QCardContent>
            <NetworkInfoMap
              data={countryData}
              highRiskCountries={highRiskCountries}
              highRiskCountriesError={highRiskCountriesError}
              isLoading={isLoading}
            />
          </QCardContent>
        </QCard>
      </Box>
      <QTabContext value={activeTab}>
        <QTabList
          onChange={handleTabChange}
          sx={{
            '.MuiTabs-indicator': {
              top: 0, // Moves the indicator to the top
              bottom: 'unset', // Removes default bottom positioning
            },
            '.MuiTab-root': {
              paddingTop: '16px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#757575',
              minWidth: '226px',
            },
            '.Mui-selected': {
              backgroundColor: '#FAFAFA',
              color: '#000000',
              fontWeight: '800',
            },
            backgroundColor: '#EEEEEE',
            borderBottom: 'none',
            marginTop: '40px',
          }}
        >
          <QTab label="All Domains" value={0} />
          <QTab label="Social Networks" value={1} />
          <QTab label="Ad Networks" value={2} />
        </QTabList>
        <QTabPanel value={0} sx={{ padding: 0, minHeight: tabPanelHeight }}>
          {loading ? (
            <PageLoader />
          ) : (
            <div id="domains-table">
              {paginationOptions.total > pageSizeOptions[0].value && (
                <QAsyncTablePagination
                  pagination={{
                    lastPage: paginationOptions.lastPage,
                    totalData: paginationOptions.total,
                    dataPerPage: pageSize,
                  }}
                  getPage={handleChangePage}
                  page={page}
                  showRowsPerPage
                  currentRows={domains.slice((page - 1) * pageSize, page * pageSize).length}
                  handlePageSizeChange={handlePageSizeChange}
                  pageSize={pageSize}
                  pageSizeOptions={pageSizeOptions}
                />
              )}
              <QTable>
                <QTableBody>
                  {_.isEmpty(domains) ? (
                    <EmptyDataRow title="Domains" />
                  ) : (
                    paginationOptions.total > 0 &&
                    domains?.slice((page - 1) * pageSize, page * pageSize).map(({ domain, country, isHighRisk }, index) => (
                      <QTableRow key={`${domain}-${country}-icon`} hoverDisabled>
                        <QTableCell>
                          {country === 'worldwide' ? (
                            <PublicIcon fontSize="small" sx={{ verticalAlign: 'middle', margin: 'auto 8px auto auto', fontSize: '29px' }} />
                          ) : (
                            <StyledCountryFlag countryCode={country} svg style={{ width: '29px', height: '22px' }} />
                          )}

                          <span className={isHighRisk ? 'overlay-high-risk-country-name' : ''}>{domain}</span>
                        </QTableCell>
                      </QTableRow>
                    ))
                  )}
                </QTableBody>
              </QTable>
              {paginationOptions.total > pageSizeOptions[0].value && (
                <QAsyncTablePagination
                  pagination={{
                    lastPage: paginationOptions.lastPage,
                    totalData: paginationOptions.total,
                    dataPerPage: pageSize,
                  }}
                  getPage={handleChangePage}
                  page={page}
                  showRowsPerPage
                  currentRows={domains.slice((page - 1) * pageSize, page * pageSize).length}
                  handlePageSizeChange={handlePageSizeChange}
                  pageSize={pageSize}
                  pageSizeOptions={pageSizeOptions}
                />
              )}
            </div>
          )}
        </QTabPanel>
        <QTabPanel value={1} sx={{ padding: 0, minHeight: tabPanelHeight }}>
          <QTable>
            <QTableBody>
              {_.isEmpty(appDetails?.socialNetworks) ? (
                <EmptyDataRow title="Social Networks" />
              ) : (
                appDetails?.socialNetworks.map((network: string) => (
                  <QTableRow key={network} hoverDisabled>
                    <QTableCell>
                      <img
                        src={`/icons/${network.toLowerCase()}.png`}
                        alt={network}
                        height="24"
                        width="24"
                        style={{ margin: '8px 8px -8px 0' }}
                      />
                      {network}
                    </QTableCell>
                  </QTableRow>
                ))
              )}
            </QTableBody>
          </QTable>
        </QTabPanel>
        <QTabPanel value={2} sx={{ padding: 0, minHeight: tabPanelHeight }}>
          <QTable>
            <QTableBody>
              {_.isEmpty(appDetails?.adNetworks) ? (
                <EmptyDataRow title="Ad Networks" />
              ) : (
                appDetails?.adNetworks.map((network: string) => (
                  <QTableRow key={network} hoverDisabled>
                    <QTableCell>{network}</QTableCell>
                  </QTableRow>
                ))
              )}
            </QTableBody>
          </QTable>
        </QTabPanel>
      </QTabContext>
    </Box>
  );
};

const StyledCountryFlag = styled(CountryFlag)`
  margin-right: 8px;
  border: 1px solid #ccc;
`;
